import { useState } from 'react';
import { TriangleAlert, Zap } from 'lucide-react';
import type { WorkflowDetail, WorkflowGraph, WorkflowProblem, WorkflowStep } from '@vagusx/clients';
import { WORKFLOW_PURPOSES } from '@vagusx/shared';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/custom/multi-select';
import { channelNotice, factLabel, walkOrder } from './graph';

/**
 * What belongs to the whole workflow: its name, the calls it answers, and
 * what a caller can do at any point in the call.
 *
 * PURPOSE IS WHAT CONNECTS A WORKFLOW TO CALLS, and only a few exist.
 * The worker maps each call to one purpose and runs the practice's
 * workflow for it; a workflow with any other purpose is never run, so
 * the choice is a list with a plain "nothing calls this" for the rest.
 */

const NONE = '__none__';

/** The fact `end_call` records, which is what "they want to end the call" waits on. */
const CALL_ENDED = 'callEnded';

export interface WorkflowPatch {
  name?: string;
  description?: string;
  intent?: string | null;
  close?: string | null;
  closeWhen?: string[] | null;
  anyTime?: WorkflowGraph['anyTime'] | null;
}

export function WorkflowSettings({
  detail,
  steps,
  problems,
  ops,
  busy,
  onSave,
}: {
  detail: WorkflowDetail;
  steps: ReadonlyMap<string, WorkflowStep>;
  problems: readonly WorkflowProblem[];
  ops: boolean;
  busy: boolean;
  onSave: (patch: WorkflowPatch) => void;
}): React.ReactElement {
  const [name, setName] = useState(detail.name);
  const [description, setDescription] = useState(detail.description);
  const purposes = WORKFLOW_PURPOSES.filter((p) => p.direction === detail.direction);
  const known = purposes.some((p) => p.intent === detail.intent);
  const notice = channelNotice(detail.channel);
  const order = walkOrder(detail.draft);

  return (
    <div className="space-y-6 p-4">
      <div>
        <h3 className="font-medium">Workflow</h3>
        <p className="text-sm text-muted-foreground">
          {detail.direction === 'inbound' ? 'Inbound' : 'Outbound'} ·{' '}
          {detail.channel === 'voice' ? 'phone call' : detail.channel}
        </p>
      </div>

      {notice && <Notice>{notice}</Notice>}

      <section className="space-y-3">
        <div className="space-y-1">
          <div className="text-sm font-medium">Name</div>
          <Input
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name !== detail.name && onSave({ name: name.trim() })}
          />
        </div>
        <div className="space-y-1">
          <div className="text-sm font-medium">Description</div>
          <Textarea
            value={description}
            rows={2}
            disabled={busy}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => description !== detail.description && onSave({ description })}
          />
        </div>
        <div className="space-y-1">
          <div className="text-sm font-medium">Calls it answers</div>
          <Select
            value={detail.intent ?? NONE}
            disabled={busy}
            onValueChange={(v) => onSave({ intent: v === NONE ? null : v })}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {purposes.map((p) => (
                <SelectItem key={p.intent} value={p.intent}>
                  {p.label}
                </SelectItem>
              ))}
              {!known && detail.intent && (
                <SelectItem value={detail.intent}>{detail.intent}</SelectItem>
              )}
              <SelectItem value={NONE}>None yet</SelectItem>
            </SelectContent>
          </Select>
          {!known && (
            <p className="text-xs text-muted-foreground">
              No call runs this workflow until it answers one of the calls above. A practice has one
              workflow per kind of call; the new one takes over when it is published.
            </p>
          )}
        </div>
      </section>

      <section className="space-y-3 border-t pt-4">
        <div>
          <h4 className="text-sm font-medium">At any point in the call</h4>
          <p className="text-xs text-muted-foreground">
            Callers can do these during any step, so they are set once for the whole call. The step
            you choose shows an “Any time” badge on the canvas.
          </p>
        </div>
        {(
          [
            ['handoff', 'They ask to speak to a person'],
            ['callback', 'They ask us to call back later'],
          ] as const
        ).map(([exit, label]) => (
          <AnyTimeRow
            key={exit}
            label={label}
            value={detail.draft.anyTime?.[exit] ?? null}
            order={order}
            steps={steps}
            busy={busy}
            onChange={(to) => {
              const anyTime = { ...detail.draft.anyTime };
              if (to) anyTime[exit] = to;
              else delete anyTime[exit];
              onSave({ anyTime: Object.keys(anyTime).length ? anyTime : null });
            }}
          />
        ))}
        <AnyTimeRow
          label="They want to end the call"
          value={detail.draft.close ?? null}
          order={order}
          steps={steps}
          busy={busy}
          onChange={(to) =>
            onSave(
              to
                ? { close: to, closeWhen: detail.draft.closeWhen ?? [CALL_ENDED] }
                : { close: null, closeWhen: null }
            )
          }
        />
        {ops && detail.draft.close && (
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">
              Also go there when any of these is recorded (VagusX only)
            </div>
            <MultiSelect
              options={[
                ...new Set([...(detail.draft.closeWhen ?? []), ...detail.factsAnywhere]),
              ].map((f) => ({
                value: f,
                label: factLabel(f),
                ...(detail.factsAnywhere.includes(f)
                  ? {}
                  : { description: 'Nothing in this workflow records this' }),
              }))}
              selected={detail.draft.closeWhen ?? []}
              disabled={busy}
              placeholder="Nothing"
              onChange={(v) => onSave({ closeWhen: v.length ? v : null })}
            />
          </div>
        )}
      </section>

      {problems.length > 0 && (
        <section className="space-y-1 border-t pt-4">
          <h4 className="text-sm font-medium">Problems with the whole workflow</h4>
          {problems.map((p, i) => (
            <p
              key={i}
              className={
                p.severity === 'warning'
                  ? 'text-xs text-muted-foreground'
                  : 'text-xs text-destructive'
              }
            >
              {p.message}
            </p>
          ))}
        </section>
      )}
    </div>
  );
}

function AnyTimeRow({
  label,
  value,
  order,
  steps,
  busy,
  onChange,
}: {
  label: string;
  value: string | null;
  order: readonly WorkflowGraph['nodes'][number][];
  steps: ReadonlyMap<string, WorkflowStep>;
  busy: boolean;
  onChange: (to: string | null) => void;
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-xs">
        <Zap className="h-3.5 w-3.5 text-muted-foreground" />
        {label}
      </div>
      <Select
        value={value ?? NONE}
        disabled={busy}
        onValueChange={(v) => onChange(v === NONE ? null : v)}
      >
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Nothing: carry on with the call</SelectItem>
          {order.map((n) => (
            <SelectItem key={n.id} value={n.id}>
              {steps.get(n.stepId)?.name ?? n.id}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Notice({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex gap-2 rounded-md border px-3 py-2 text-xs text-muted-foreground">
      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}
